// Material UI
import { Box, Grid, Paper, Typography } from '@mui/material';
import SwapVertIcon from '@mui/icons-material/SwapVert';

// Components
import ProductsTable from 'pages/event/products/admission/ProductsTable';
import { LoadingState, ErrorState } from 'components/ui/states';
import { ActionButton } from 'components/ui/buttons';
import Tooltip from 'components/popups/Tooltip';
import {
    matchesProductFilter,
    shouldShowProductList,
    shouldShowProductType,
} from '../productFilters';

const PRODUCT_TYPES = {
    admission: 1,
    addon: 2,
};

export default function ProductList({
    event,
    status,
    products,
    displayProductTypes,
    searchText,
    selectedFilter,
    compact = false,
    onProductEdit,
    onProductCopy,
    onOpenReorder,
}) {
    if (!shouldShowProductList(selectedFilter)) {
        return null;
    }

    if (status === 'loading') {
        return (
            <Box mb={8}>
                <LoadingState />
            </Box>
        );
    }

    if (status === 'error') {
        return (
            <Box mb={8}>
                <ErrorState message="Unable to find tickets" />
            </Box>
        );
    }

    const normalizedSearchText = searchText.trim().toLowerCase();
    const matchesSearch = (product) =>
        product.prod_name?.toLowerCase().includes(normalizedSearchText);
    const matchesFilter = (product) =>
        matchesProductFilter(product, selectedFilter, event?.time_zone);
    const admissionProducts = products.filter(
        (product) => product.prod_type_id === PRODUCT_TYPES.admission,
    );
    const addonProducts = products.filter(
        (product) => product.prod_type_id === PRODUCT_TYPES.addon,
    );
    const filteredAdmissionProducts = admissionProducts.filter(matchesSearch).filter(matchesFilter);
    const filteredAddonProducts = addonProducts.filter(matchesSearch).filter(matchesFilter);
    const showAdmission =
        displayProductTypes.includes(PRODUCT_TYPES.admission) &&
        shouldShowProductType(PRODUCT_TYPES.admission, selectedFilter);
    const showAddon =
        displayProductTypes.includes(PRODUCT_TYPES.addon) &&
        shouldShowProductType(PRODUCT_TYPES.addon, selectedFilter);

    return (
        <Box mb={8}>
            <Paper display="flex" flexDirection="column">
                {showAdmission && (
                    <>
                        <ProductSection
                            helperText="Tickets used for event entry and attendance."
                            onOpenReorder={() => onOpenReorder('admission')}
                            enableReorder={admissionProducts.length > 1}
                            title="Admission Tickets"
                            tooltip="Choose the order your ticket types appear on the purchase page"
                        />
                        <ProductsTable
                            addon={false}
                            event={event}
                            products={filteredAdmissionProducts}
                            compact={compact}
                            onProductSelect={(id) => onProductEdit(id, 'admission')}
                            onProductCopy={(id) => onProductCopy(id, 'admission')}
                        />
                    </>
                )}

                {showAddon && (
                    <>
                        <ProductSection
                            helperText="Extras such as parking, camping, or merchandise. Add-ons do not count toward attendance."
                            onOpenReorder={() => onOpenReorder('addon')}
                            enableReorder={addonProducts.length > 1}
                            title="Add-On Tickets"
                            tooltip="Choose the order your ticket types appear on the purchase page"
                        />
                        <ProductsTable
                            addon={true}
                            event={event}
                            products={filteredAddonProducts}
                            compact={compact}
                            onProductSelect={(id) => onProductEdit(id, 'addon')}
                            onProductCopy={(id) => onProductCopy(id, 'addon')}
                        />
                    </>
                )}
            </Paper>
        </Box>
    );
}

function ProductSection(props) {
    const { helperText, onOpenReorder, enableReorder, title, tooltip } = props;

    return (
        <Grid container p={2} py={1} alignItems="center" bgcolor={'#eee'}>
            <Grid item xs={12} sm={7}>
                <Typography variant="overline" fontWeight="bold" lineHeight={1.2}>
                    {title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {helperText}
                </Typography>
            </Grid>

            {enableReorder && (
                <Grid item xs={12} sm={5} display="flex" justifyContent="flex-end">
                    <Tooltip message={tooltip}>
                        <span>
                            <ActionButton
                                variant="text"
                                startIcon={<SwapVertIcon />}
                                onClick={onOpenReorder}
                            >
                                Set Display Order
                            </ActionButton>
                        </span>
                    </Tooltip>
                </Grid>
            )}
        </Grid>
    );
}
